'use client'
import { createTextarea } from '@gluestack-ui/core/textarea/creator'
import type { VariantProps } from '@gluestack-ui/utils/nativewind-utils'
import { tva } from '@gluestack-ui/utils/nativewind-utils'
import { useStyleContext, withStyleContext } from '@gluestack-ui/utils/nativewind-utils'
import React from 'react'
import { TextInput, View } from 'react-native'

const SCOPE = 'TEXTAREA'
const UITextarea = createTextarea({
    Root: withStyleContext(View, SCOPE),
    Input: TextInput,
})

// Structural scaffolding only — all presentation lives in `components/textarea`
// (the single source of truth, matching the Figma Textarea states). The
// gluestack interaction wiring (data-[hover|focus|invalid|disabled]) is kept.
const textareaStyle = tva({
    base: 'w-full',
    variants: {
        variant: { default: '' },
        size: { sm: '', md: '', lg: '', xl: '' },
    },
})

const textareaInputStyle = tva({
    base: 'flex-1 web:outline-0 web:outline-none web:cursor-text web:data-[disabled=true]:cursor-not-allowed',
    parentVariants: {
        size: { sm: '', md: '', lg: '', xl: '' },
    },
})

type ITextareaProps = React.ComponentProps<typeof UITextarea> & VariantProps<typeof textareaStyle>

const Textarea = React.forwardRef<React.ComponentRef<typeof UITextarea>, ITextareaProps>(
    function Textarea({ className, variant = 'default', size = 'md', ...props }, ref) {
        return (
            <UITextarea
                ref={ref}
                {...props}
                className={textareaStyle({ variant, class: className })}
                context={{ size }}
            />
        )
    },
)

type ITextareaInputProps = React.ComponentProps<typeof UITextarea.Input> &
    VariantProps<typeof textareaInputStyle>

// AOS layouts use fixed Figma pixel tokens, not Dynamic Type-scaled text —
// default allowFontScaling off below, callers can still opt back in explicitly.
const TextareaInput = React.forwardRef<
    React.ComponentRef<typeof UITextarea.Input>,
    ITextareaInputProps
>(function TextareaInput({ className, allowFontScaling = false, ...props }, ref) {
    const { size: parentSize } = useStyleContext(SCOPE)

    return (
        <UITextarea.Input
            ref={ref}
            allowFontScaling={allowFontScaling}
            {...props}
            textAlignVertical='top'
            className={textareaInputStyle({
                parentVariants: {
                    size: parentSize,
                },
                class: className,
            })}
        />
    )
})

Textarea.displayName = 'Textarea'
TextareaInput.displayName = 'TextareaInput'

export { Textarea, TextareaInput }
